import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { pageCache } from '../utils/pageCache';
import {
  TrendingUp, TrendingDown, History, Star, Award, BarChart3, Upload, Sparkles,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { fmt, fmtK, fmtCents, fmtCount, fmtChange, fmtChangeOrNew } from '../utils/formatters';
import LoadingScreen from '../components/LoadingScreen';

/** The growth chart's tooltip. A month after a $0 month has no growth
 *  percent (null): it reads "new" when the month sold (math audit row 6). */
export const growthTooltip = (value: number | null, _name: string, item: any): [string, string] =>
  [fmtChangeOrNew(value, item?.payload?.revenue ?? 0), 'Growth'];


export default function HistoryPage() {
  const [data, setData] = useState<any>(() => pageCache.get('/api/history') ?? null);
  const [loading, setLoading] = useState(() => !pageCache.has('/api/history'));
  const navigate = useNavigate();

  useEffect(() => {
    const url = '/api/history';
    if (pageCache.has(url)) {
      setData(pageCache.get(url));
      setLoading(false);
    }
    axios.get(url)
      .then((res) => { pageCache.set(url, res.data); setData(res.data); })
      .catch(() => { if (!pageCache.has(url)) setData(null); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
        <div className="mb-8">
          <h1 className="font-serif text-2xl text-[#2D2A2E]">History</h1>
        </div>
        <LoadingScreen fullScreen={false} />
      </motion.div>
    );
  }

  if (!data?.has_data) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
        <div className="mb-8">
          <h1 className="font-serif text-2xl text-[#2D2A2E]">History</h1>
        </div>
        <div className="flex flex-col items-center justify-center py-24">
          <History size={48} strokeWidth={1.5} className="text-[#4A2D5E]/30 mb-4" />
          <h3 className="font-serif text-xl text-[#2D2A2E] mb-2">No history yet</h3>
          <p className="font-sans text-sm text-[#6B6670] mb-6 text-center max-w-sm">
            Upload transaction data to start building your business history and unlock interesting insights over time.
          </p>
          <button
            onClick={() => navigate('/settings/data')}
            className="bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors"
          >
            Upload Data
          </button>
        </div>
      </motion.div>
    );
  }

  const { alltime, monthly_trend, best_month, top_products, streaks, growth, facts } = data;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
      <div className="mb-8">
        <h1 className="font-serif text-2xl text-[#2D2A2E]">History</h1>
      </div>

      {/* All-Time KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
          <div className="text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">Total Revenue</div>
          <div className="text-2xl font-sans font-bold text-[#2D2A2E] tabular-nums">{fmt(alltime.revenue)}</div>
        </div>
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
          <div className="text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">Gross profit (after COGS)</div>
          <div className="text-2xl font-sans font-bold text-[#2D2A2E] tabular-nums">{fmt(alltime.profit)}</div>
        </div>
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
          <div className="text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">All-time {alltime.orders_label || 'Transactions'}</div>
          <div className="text-2xl font-sans font-bold text-[#2D2A2E] tabular-nums">{fmtCount(alltime.transactions)}</div>
        </div>
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
          <div className="text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">{alltime.avg_label || 'Avg Order Value'}</div>
          <div className="text-2xl font-sans font-bold text-[#2D2A2E] tabular-nums">{fmtCents(alltime.avg_order)}</div>
        </div>
      </div>

      {/* Interesting Facts */}
      {facts.length > 0 && (
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-5 shadow-card mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} strokeWidth={1.5} className="text-[#2E6B4F]" />
            <h3 className="font-serif text-base text-[#2D2A2E]">Your Numbers Tell a Story</h3>
          </div>
          <div className="space-y-2">
            {facts.map((fact: string, i: number) => (
              <div key={i} className="flex items-start gap-2">
                <Star size={12} strokeWidth={1.5} className="text-[#4A2D5E] mt-1 flex-shrink-0" />
                <p className="font-sans text-sm text-[#6B6670] leading-relaxed">{fact}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Revenue Trend */}
      {monthly_trend.length > 1 && (
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card mb-8">
          <h3 className="font-serif text-lg text-[#2D2A2E] mb-4">Monthly Revenue Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthly_trend}>
              <defs>
                <linearGradient id="histRevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4A2D5E" stopOpacity={0.12} />
                  <stop offset="95%" stopColor="#4A2D5E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DE" />
              <XAxis dataKey="month" tick={{ fill: '#6B6670', fontSize: 11, fontFamily: 'DM Sans' }} />
              <YAxis tick={{ fill: '#6B6670', fontSize: 11, fontFamily: 'DM Sans' }} tickFormatter={(v) => fmtK(v)} />
              <Tooltip
                contentStyle={{ borderRadius: '10px', border: '1px solid #E6E2DE', fontFamily: 'DM Sans', fontSize: 12 }}
                formatter={(value: number, name: string) => [fmt(value), name === 'revenue' ? 'Revenue' : 'Profit']}
              />
              <Area type="monotone" dataKey="revenue" stroke="#4A2D5E" strokeWidth={2} fill="url(#histRevGrad)" />
              <Area type="monotone" dataKey="profit" stroke="#2E6B4F" strokeWidth={1.5} fill="none" strokeDasharray="5 3" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Growth Rate */}
      {growth.length > 0 && (
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card mb-8">
          <h3 className="font-serif text-lg text-[#2D2A2E] mb-4">Month-over-Month Growth</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DE" />
              <XAxis dataKey="month" tick={{ fill: '#6B6670', fontSize: 11, fontFamily: 'DM Sans' }} />
              <YAxis tick={{ fill: '#6B6670', fontSize: 11, fontFamily: 'DM Sans' }} tickFormatter={(v) => fmtChange(v)} />
              <Tooltip
                contentStyle={{ borderRadius: '10px', border: '1px solid #E6E2DE', fontFamily: 'DM Sans', fontSize: 12 }}
                formatter={growthTooltip as any}
              />
              <Bar dataKey="growth" radius={[6, 6, 0, 0]} fill="#4A2D5E">
                {growth.map((entry: any, index: number) => (
                  <rect key={index} fill={entry.growth == null ? '#6B6670' : entry.growth >= 0 ? '#2E6B4F' : '#B54A4A'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* All-Time Top Products */}
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] overflow-hidden shadow-card">
          <div className="p-6 border-b border-[#E6E2DE]">
            <div className="flex items-center gap-2">
              <Award size={18} strokeWidth={1.5} className="text-[#4A2D5E]" />
              <h3 className="font-serif text-lg text-[#2D2A2E]">All-Time Top Products</h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#FAF9F7] border-b border-[#E6E2DE]">
                  <th className="text-left p-3 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans">#</th>
                  <th className="text-left p-3 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans">Product</th>
                  <th className="text-right p-3 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans">Revenue</th>
                  <th className="text-right p-3 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans">Units</th>
                </tr>
              </thead>
              <tbody>
                {top_products.map((p: any, i: number) => (
                  <tr key={p.name} className="border-b border-[#E6E2DE] last:border-0 hover:bg-[#FAF9F7] transition-colors">
                    <td className="p-3 text-sm font-sans text-[#6B6670] tabular-nums">
                      {i + 1}
                    </td>
                    <td className="p-3">
                      <span className="text-sm font-sans text-[#2D2A2E] font-medium">{p.name}</span>
                      <span className="ml-2 text-[10px] font-sans font-medium px-1.5 py-0.5 rounded-full bg-[#4A2D5E]/10 text-[#4A2D5E]">{p.category}</span>
                    </td>
                    <td className="p-3 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmt(p.revenue)}</td>
                    <td className="p-3 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmtCount(p.units)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Streaks + Best Month */}
        <div className="space-y-6">
          {/* Best Month */}
          {best_month && (
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={18} strokeWidth={1.5} className="text-[#2E6B4F]" />
                <h3 className="font-serif text-base text-[#2D2A2E]">Best Month Ever</h3>
              </div>
              <div className="text-3xl font-sans font-bold text-[#2E6B4F] tabular-nums">{fmt(best_month.revenue)}</div>
              <div className="font-sans text-sm text-[#6B6670] mt-1">{best_month.month}</div>
            </div>
          )}

          {/* Product Streaks */}
          {streaks.length > 0 && (
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <div className="flex items-center gap-2 mb-3">
                <Award size={18} strokeWidth={1.5} className="text-[#4A2D5E]" />
                <h3 className="font-serif text-base text-[#2D2A2E]">#1 Product Streaks</h3>
              </div>
              <div className="space-y-3">
                {streaks.map((s: any, i: number) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-[#E6E2DE] last:border-0">
                    <span className="font-sans text-sm text-[#2D2A2E] font-medium">{s.product}</span>
                    <span className="font-sans text-sm text-[#4A2D5E] font-medium tabular-nums">{s.months} months</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
